import React, { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { feedbackApi } from "../../services/feedbackApi";
import { getDiagnostics } from "../../services/diagnostics";

interface Props {
  onSubmitted: () => void;
}

export default function SubmitTicket({ onSubmitted }: Props) {
  const { user } = useAuth();
  const [form, setForm] = useState({
    type: "bug" as "bug" | "feature",
    title: "",
    description: "",
    priority: "medium" as "low" | "medium" | "high" | "critical",
  });
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<{ type: "success" | "error"; message: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus(null);
    setSubmitting(true);

    try {
      const payload: Record<string, unknown> = {
        ...form,
        organizationId: user?.organizationId || "",
        submittedBy: `${user?.firstName} ${user?.lastName}`,
        submittedByEmail: user?.email || "",
      };

      // Attach diagnostics for bug reports
      if (form.type === "bug") {
        payload.diagnostics = getDiagnostics();
      }

      await feedbackApi.submit(payload as any);
      setStatus({ type: "success", message: "Submitted successfully! We'll review it soon." });
      setForm({ type: "bug", title: "", description: "", priority: "medium" });
      setTimeout(onSubmitted, 1500);
    } catch (err) {
      setStatus({ type: "error", message: err instanceof Error ? err.message : "Failed to submit" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="settings-section">
        <h2>{form.type === "bug" ? "🐛 Report a Bug" : "💡 Request a Feature"}</h2>
        <p className="section-desc">
          {form.type === "bug"
            ? "Describe what went wrong — browser info and recent errors are captured automatically"
            : "Tell us what you'd like to see in the platform"}
        </p>

        {status && (
          <div style={{ marginBottom: "1rem" }}>
            <span className={`save-status ${status.type}`}>{status.message}</span>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="settings-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="fb-type">Type</label>
                <select id="fb-type" value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value as any }))}>
                  <option value="bug">🐛 Bug Report</option>
                  <option value="feature">💡 Feature Request</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="fb-priority">Priority</label>
                <select id="fb-priority" value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value as any }))}>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="fb-title">Title</label>
              <input
                id="fb-title"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder={form.type === "bug" ? "Sign-in page crashes on mobile" : "Add dark mode to dashboard"}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="fb-desc">Description</label>
              <textarea
                id="fb-desc"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder={form.type === "bug" ? "Steps to reproduce:\n1. Go to...\n2. Click...\n3. See error..." : "It would be great if..."}
                required
                rows={6}
                style={{ width: "100%", padding: "0.6rem 0.8rem", border: "1px solid #d1d5db", borderRadius: "6px", fontFamily: "inherit", fontSize: "0.9rem", resize: "vertical" }}
              />
            </div>

            {form.type === "bug" && (
              <div style={{ background: "#f0f9ff", padding: "0.75rem 1rem", borderRadius: "6px", fontSize: "0.85rem", color: "#1e40af" }}>
                ℹ️ The following will be attached automatically: browser info, screen size, current page, recent console errors, and failed network requests.
              </div>
            )}

            <div className="settings-actions">
              <button type="submit" className="btn btn-primary" disabled={submitting} style={{ width: "auto" }}>
                {submitting ? "Submitting..." : "Submit"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
